import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Empty, Loader, btn } from "@/components/Page";
import BattleReplay from "@/components/BattleReplay";
import { Link } from "react-router-dom";
import { ExpeditionKit, RunKitLoadout } from "@/components/ExpeditionKit";

const TACTICS_CONFIG = [
  { id: "aggressive", label: "Aggressive", desc: "+10% attack, −10% defense. Edges out balanced opponents." },
  { id: "balanced",   label: "Balanced",   desc: "Solid all-round. Counters aggressive and defensive evenly." },
  { id: "defensive",  label: "Defensive",  desc: "+10% defense, −10% attack. Walls out aggressive pressure." },
];

function RewardLine({ rewards }) {
  const fragments = (rewards?.fragments || []).map(
    (f) => `${f.amount} ${f.rarity} ${f.kind} fragments`
  );
  const equipment = rewards?.equipment
    ? `${rewards.equipment.rarity} ${rewards.equipment.kind}`
    : "";
  const tickets = Object.entries(rewards?.tickets || {}).map(
    ([type, amount]) => `${amount} ${type.replace("_", " ")}`
  );
  const parts = [
    rewards?.gold   ? `${rewards.gold} gold`     : "",
    rewards?.follis ? `${rewards.follis} follis`  : "",
    rewards?.expedition_marks ? `${rewards.expedition_marks} Expedition Marks` : "",
    ...fragments,
    equipment,
    ...tickets,
  ].filter(Boolean);
  return (
    <div className="text-xs leading-relaxed text-zinc-300">{parts.join(" · ")}</div>
  );
}

function rewardOutcomeItems(rewards) {
  const totals = { gold: 0, follis: 0, expeditionMarks: 0, fragments: {}, tickets: {}, equipment: {} };
  for (const reward of rewards || []) {
    totals.gold += reward?.gold || 0;
    totals.follis += reward?.follis || 0;
    totals.expeditionMarks += reward?.expedition_marks || 0;
    for (const fragment of reward?.fragments || []) {
      const key = `${fragment.rarity}-${fragment.kind}`;
      totals.fragments[key] = (totals.fragments[key] || 0) + fragment.amount;
    }
    for (const [ticket, amount] of Object.entries(reward?.tickets || {})) {
      totals.tickets[ticket] = (totals.tickets[ticket] || 0) + amount;
    }
    if (reward?.equipment) {
      const key = `${reward.equipment.rarity}-${reward.equipment.kind}`;
      totals.equipment[key] = (totals.equipment[key] || 0) + 1;
    }
  }
  return [
    totals.gold ? `${totals.gold} gold` : "",
    totals.follis ? `${totals.follis} follis` : "",
    totals.expeditionMarks ? `${totals.expeditionMarks} Expedition Marks` : "",
    ...Object.entries(totals.fragments).map(([key, amount]) => `${amount} ${key.replace("-", " ")} fragments`),
    ...Object.entries(totals.tickets).map(([ticket, amount]) => `${amount} ${ticket.replaceAll("_", " ")}`),
    ...Object.entries(totals.equipment).map(([key, amount]) => `${amount > 1 ? `${amount}× ` : ""}${key.replace("-", " ")}`),
  ].filter(Boolean);
}

function TerminalOutcome({ run, resetRemaining, battle, onReplay, adminTestMode, busy, onAdminRestart }) {
  const status = run.status;
  const result = run.result || {};
  const finalFight = Math.min(5, Number(result.final_fight || (status === "lost" ? run.depth + 1 : run.depth)) || 0);
  const championFight = result.champion || finalFight >= 5;
  const retained = [...(run.claimed || []), ...(result.champion_cache ? [result.champion_cache] : [])];
  const retainedItems = rewardOutcomeItems(retained);
  const details = status === "lost"
    ? { heading: "Run Ended — Defeated", encounter: championFight ? "Defeated in the Champion Bout" : `Defeated in Fight ${finalFight} of 5`, tone: "border-red-800/60 bg-red-950/20", headingTone: "text-red-200" }
    : status === "cashed_out"
      ? { heading: "Run Ended — Cashed Out", encounter: `Cashed out after Fight ${finalFight} of 5`, tone: "border-amber-800/60 bg-amber-950/20", headingTone: "text-amber-200" }
      : { heading: "Run Complete", encounter: "Champion Bout completed", tone: "border-emerald-800/60 bg-emerald-950/20", headingTone: "text-emerald-200" };

  return (
    <div className="space-y-4">
      <section data-testid="sands-result" className={`border p-5 ${details.tone}`}>
        <h3 data-testid="sands-terminal-heading" className={`font-cinzel text-2xl ${details.headingTone}`}>{details.heading}</h3>
        <p data-testid="sands-terminal-encounter" className="mt-2 text-sm text-zinc-100">{details.encounter}</p>
        {result.message && <p data-testid="sands-terminal-message" className="mt-1 text-sm text-zinc-400">{result.message}</p>}
        {result.expedition_marks > 0 && <p data-testid="sands-terminal-marks" className="mt-2 text-sm text-amber-200">Expedition Marks secured this run: {result.expedition_marks}</p>}
        {result.lost_expedition_marks > 0 && <p data-testid="sands-terminal-marks-lost" className="mt-2 text-sm text-red-300">Expedition Marks lost at risk: {result.lost_expedition_marks}</p>}
      </section>

      <section data-testid="sands-reward-outcome" className="border border-amber-800/50 bg-zinc-950/80 p-5">
        <div className="text-xs font-cinzel uppercase tracking-[0.16em] text-amber-300">Reward outcome</div>
        {retainedItems.length ? (
          <>
            <p data-testid="sands-reward-outcome-status" className="mt-2 text-sm text-emerald-300">Rewards secured and added to your account</p>
            <div data-testid="sands-retained-rewards" className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-sm text-zinc-200">
              {retainedItems.map((item) => <span key={item}>{item}</span>)}
            </div>
          </>
        ) : (
          <p data-testid="sands-reward-outcome-status" className="mt-2 text-sm text-zinc-300">No rewards were secured this run.</p>
        )}
        {status === "lost" && result.lost_at_risk && (
          <p data-testid="sands-at-risk-rewards-lost" className="mt-3 border-t border-red-900/40 pt-3 text-sm text-red-300">At-risk rewards were lost when the run ended.</p>
        )}
      </section>

      <p data-testid="sands-next-entry-countdown" className="text-xs tabular-nums text-zinc-500">Next free entry at 1 AM server time · {resetCountdown(resetRemaining)}</p>
      {battle && <button data-testid="sands-watch-last-replay-button" onClick={onReplay} className={`${btn("ghost")} text-xs`}>Watch last fight</button>}
      {adminTestMode && <button data-testid="sands-admin-restart-button" disabled={busy} onClick={onAdminRestart} className={btn("ghost")}>Admin: Choose a New Run</button>}
    </div>
  );
}

function resetCountdown(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = String(Math.floor(s / 3600)).padStart(2, "0");
  const m = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const sec = String(s % 60).padStart(2, "0");
  return `${h}:${m}:${sec}`;
}

function TacticsSelector({ tactics, setTactics }) {
  return (
    <div className="mb-6">
      <div className="mb-3 text-xs uppercase tracking-[0.16em] text-zinc-500">Your tactics for this encounter</div>
      <div className="grid grid-cols-3 gap-3">
        {TACTICS_CONFIG.map((t) => (
          <button
            key={t.id}
            data-testid={`sands-tactics-${t.id}`}
            onClick={() => setTactics(t.id)}
            className={`border p-3 text-left transition-all duration-150 ${
              tactics === t.id
                ? "border-amber-500 bg-amber-950/25 text-amber-200 shadow-[0_0_8px_rgba(245,158,11,0.15)]"
                : "border-zinc-700 bg-zinc-950/50 text-zinc-400 hover:border-zinc-500 hover:text-zinc-200"
            }`}
          >
            <div className="font-cinzel text-sm uppercase tracking-wide">
              {tactics === t.id && <span className="mr-1.5 text-amber-400">▸</span>}
              {t.label}
            </div>
            <div className="mt-1.5 text-[11px] leading-relaxed text-zinc-500">{t.desc}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function ChampionCachePanel({ cache, remainingMs, run }) {
  if (!cache) return null;
  const rewardFragments = cache.rewards?.fragments || [];
  const rewardTickets = cache.rewards?.tickets || {};
  const activeRun = ["choosing", "won"].includes(run?.status);
  const completedEncounters = Math.max(0, Math.min(5, Number(run?.depth) || 0));
  const progressPercent = (completedEncounters / 5) * 100;
  return (
    <div data-testid="sands-champion-cache" className="border border-amber-700/40 bg-amber-950/15 px-4 py-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs font-cinzel uppercase tracking-[0.18em] text-amber-300">Weekly Champion Cache</div>
          <p data-testid="sands-champion-cache-status" className={`mt-1 text-sm ${cache.earned_this_week ? "text-emerald-300" : "text-zinc-300"}`}>
            {cache.earned_this_week ? "Earned this week" : "Complete all 5 encounters to earn this week’s cache."}
          </p>
        </div>
        <div data-testid="sands-champion-cache-reset" className="text-right text-xs tabular-nums text-amber-200">
          <div>Weekly reset</div>
          <div className="mt-1">Monday 12 AM server time · {resetCountdown(remainingMs)}</div>
        </div>
      </div>
      <div data-testid="sands-champion-cache-rewards" className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-300">
        <span>{rewardTickets.venatore_tickets || 0} Venatore Ticket</span>
        <span>{rewardTickets.breeding_tickets || 0} Breeding Ticket</span>
        {rewardFragments.map((fragment) => (
          <span key={`${fragment.rarity}-${fragment.kind}`}>{fragment.amount} {fragment.rarity} {fragment.kind} fragments</span>
        ))}
      </div>
      {activeRun && (
        <div data-testid="sands-champion-cache-progress" className="mt-3 border-t border-amber-900/40 pt-3">
          <div className="flex items-center justify-between gap-3 text-xs">
            <span className="uppercase tracking-[0.16em] text-zinc-500">Five-fight progress</span>
            <span data-testid="sands-champion-cache-progress-value" className="tabular-nums text-amber-200">{completedEncounters} of 5 completed</span>
          </div>
          <div data-testid="sands-champion-cache-progress-track" className="mt-2 h-1.5 overflow-hidden bg-zinc-800">
            <div
              data-testid="sands-champion-cache-progress-bar"
              className="h-full bg-amber-500 transition-[width] duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function ThisWeeksChampions({ champions }) {
  if (!champions?.length) return null;
  return (
    <section data-testid="sands-week-champions" className="border-t border-amber-900/40 pt-6">
      <h3 data-testid="sands-week-champions-heading" className="font-cinzel text-xl text-amber-200">This Week&rsquo;s Champions</h3>
      <div data-testid="sands-week-champions-list" className="mt-3 space-y-2 text-sm">
        {champions.map((champion) => (
          <div key={`${champion.lanista_id}-${champion.gladiator_id}`} data-testid={`sands-week-champion-${champion.gladiator_id}`} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-zinc-400">
            <Link data-testid={`sands-champion-lanista-link-${champion.lanista_id}`} to={`/profile/${champion.lanista_id}`} className="text-zinc-200 underline decoration-zinc-600 underline-offset-4 transition-colors duration-200 hover:text-amber-300 hover:decoration-amber-400">{champion.lanista_name}</Link>
            <span aria-hidden="true" className="text-zinc-600">/</span>
            <Link data-testid={`sands-champion-gladiator-link-${champion.gladiator_id}`} to={`/gladiator/${champion.gladiator_id}`} className="text-amber-300 underline decoration-amber-800 underline-offset-4 transition-colors duration-200 hover:text-amber-100 hover:decoration-amber-300">{champion.gladiator_name}</Link>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function SandsRun() {
  const [data, setData]             = useState(null);
  const [pick, setPick]             = useState("");
  const [tactics, setTactics]       = useState("balanced");
  const [battle, setBattle]         = useState(null);
  const [replayOpen, setReplayOpen] = useState(false);
  const [busy, setBusy]             = useState(false);
  const [clock, setClock]           = useState(Date.now());
  const [serverOffset, setServerOffset] = useState(0);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/sands/status");
      setData(r.data);
      setServerOffset(new Date(r.data.server_now).getTime() - Date.now());
      setPick((p) => p || r.data.gladiators?.[0]?.id || "");
      // Restore last battle from an already-active run (survives page refresh)
      if (r.data.run?.result?.battle) {
        setBattle(r.data.run.result.battle);
      }
    } catch (e) {
      toast.error(apiErr(e, "Could not prepare The Sands"));
      setData(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = window.setInterval(() => setClock(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);

  const act = async (path, body) => {
    setBusy(true);
    try {
      const r = await api.post(path, body);
      setData((d) => ({ ...d, run: r.data, kit: r.data?.kit || d.kit }));
      if (path === "/sands/fight" && r.data?.result?.battle) {
        setBattle(r.data.result.battle);
        setReplayOpen(true);           // auto-open replay only after actual fight calls
      }
      window.dispatchEvent(new Event("lanista:refresh-badges"));
    } catch (e) {
      toast.error(apiErr(e));
      await load();
    } finally {
      setBusy(false);
    }
  };

  const resetAdminRun = async () => {
    setBusy(true);
    try {
      await api.post("/sands/admin-reset");
      await load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const kitAction = async (path, success) => {
    setBusy(true);
    try {
      await api.post(path);
      toast.success(success);
      await load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const equipTool = async (slot, toolId) => {
    setBusy(true);
    try {
      await api.post("/sands/kit/equip", { slot, tool_id: toolId });
      toast.success(toolId ? "Expedition tool equipped." : "Expedition slot cleared.");
      await load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (data === false) return <Empty label="The Sands could not be prepared." />;
  if (!data)          return <Loader label="Preparing The Sands…" />;

  const run = data.run;
  const condition = run?.condition || data.condition;
  const resetRemaining = new Date(data.reset_at).getTime() - (clock + serverOffset);
  const championCache = run?.champion_cache || data.champion_cache;
  const championCacheRemaining = championCache?.reset_at
    ? new Date(championCache.reset_at).getTime() - (clock + serverOffset)
    : 0;
  const terminalRun = ["lost", "cashed_out", "complete"].includes(run?.status);
  const runActive = ["choosing", "won"].includes(run?.status);
  const lockedThroughDepth = Number(run?.locked_through_depth || (run?.result?.auto_locked ? run?.depth : 0) || 0);
  const hasUnclaimedRewards = (run?.unclaimed || []).length > 0;
  const sandsEaseReduction = Number(run?.sands_ease_reduction || (data.sands_ease_event?.active ? data.sands_ease_event.reduction_percent : 0) || 0);
  const sandsEaseActive = sandsEaseReduction > 0;
  const lockStatusText = lockedThroughDepth
    ? `Rewards secured through Fight ${lockedThroughDepth} — future fights cannot put them at risk.`
    : "";

  return (
    <section data-testid="sands-run" className="relative overflow-hidden border border-amber-900/40 bg-zinc-950 p-5 sm:p-7">
      <div
        data-testid="sands-run-background"
        className="pointer-events-none absolute inset-x-0 top-0 h-64 bg-cover bg-[position:62%_center] opacity-35 sm:h-72 sm:bg-center"
        style={{ backgroundImage: "url('/the-sands-banner.png')" }}
      />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-64 bg-gradient-to-b from-zinc-950/15 via-zinc-950/65 to-zinc-950 sm:h-72" />

      <div className="relative space-y-6">

        {/* ── Header ── */}
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-amber-400">Free daily arena run</div>
            <h2 className="mt-2 font-cinzel text-4xl text-amber-100">Sands Run</h2>
            <p className="mt-2 max-w-2xl text-sm text-zinc-300">
              Choose your opponent, protect what you have earned, and leave whenever the risk no longer feels right.
            </p>
          </div>

        </header>

        {/* ── Daily condition banner ── */}
        <div data-testid="sands-daily-condition" className="border-l-2 border-red-500 bg-red-950/30 px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-xs uppercase tracking-[0.18em] text-red-300">Today: {condition?.name}</div>
            <div data-testid="sands-reset-countdown" className="text-xs tabular-nums text-amber-200">
              Resets at 1 AM server time · {resetCountdown(resetRemaining)}
            </div>
          </div>
          <p className="mt-1 text-sm text-zinc-200">{condition?.detail}</p>
        </div>

        {sandsEaseActive && (
          <div data-testid="sands-ease-event-banner" className="border-l-2 border-cyan-500 bg-cyan-950/20 px-4 py-3">
            <div className="text-xs uppercase tracking-[0.18em] text-cyan-200">Sands Ease Event</div>
            <p data-testid="sands-ease-event-reduction" className="mt-1 text-sm text-zinc-100">
              Sands Run opponents are {sandsEaseReduction}% weaker{run ? " for this run" : ""}. Legacy Sands rewards are doubled, including armor finds. Expedition Kit reductions still stack with this event.
            </p>
          </div>
        )}

        <ChampionCachePanel cache={championCache} remainingMs={championCacheRemaining} run={run} />
        <ExpeditionKit
          kit={data.kit}
          runActive={runActive}
          busy={busy}
          onCraft={(toolId) => kitAction(`/sands/kit/${toolId}/craft`, "Expedition tool crafted.")}
          onEquip={equipTool}
        />

        {/* ── Gladiator selector (no active run) ── */}
        {!run && (
          <div className="max-w-xl border border-zinc-800 bg-black/25 p-5">
            <label className="text-xs uppercase tracking-[0.16em] text-zinc-500">Choose gladiator</label>
            <select
              data-testid="sands-gladiator-select"
              value={pick}
              onChange={(e) => setPick(e.target.value)}
              className="mt-2 w-full border border-zinc-700 bg-zinc-950 p-3 text-zinc-100"
            >
              <option value="">Select a gladiator</option>
              {data.gladiators.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name} · {g.cls} · {g.base_score !== g.score ? `${g.base_score} → ${g.score}` : g.score} strength
                </option>
              ))}
            </select>
            <button
              data-testid="sands-start-button"
              disabled={!pick || busy}
              onClick={() => act("/sands/start", { glad_id: pick })}
              className={`${btn("primary")} mt-4 w-full`}
            >
              {busy ? "Entering…" : "Enter The Sands"}
            </button>
          </div>
        )}

        {/* ── Active run ── */}
        {run && (
          <div className={terminalRun ? "" : "grid gap-6 lg:grid-cols-[1fr_19rem]"}>
            <main>

              {/* Status bar */}
              <div
                data-testid="sands-run-status"
                className="mb-5 flex flex-wrap gap-x-5 gap-y-2 border-b border-zinc-800 pb-4 text-sm"
              >
                <span>{run.glad_name}</span>
                <span data-testid="sands-current-strength">Current strength {run.glad_score}</span>
                <span>Fight {run.depth}/5</span>
                <span data-testid="sands-fatigue-impact">
                  Fatigue {run.fatigue} · {run.fatigue_impact || 0}% impact to battle performance
                </span>
                <span data-testid="sands-run-marks">Expedition Marks in run {run.marks_earned || 0}</span>
              </div>
              <RunKitLoadout tools={run.kit_snapshot} />

              {/* ── Choosing: tactics + encounter cards ── */}
              {run.status === "choosing" && (
                <div>
                  {lockedThroughDepth > 0 && (
                    <div data-testid="sands-secured-rewards-status" className="mb-4 border-l-2 border-emerald-500 bg-emerald-950/20 px-4 py-3 text-sm text-emerald-200">
                      {lockStatusText}
                    </div>
                  )}
                  <TacticsSelector tactics={tactics} setTactics={setTactics} />

                  {/* "Watch last fight" during choosing (if battle available from prev round) */}
                  {battle && (
                    <div className="mb-5">
                      <button
                        data-testid="sands-watch-prev-replay-button"
                        onClick={() => setReplayOpen(true)}
                        className={`${btn("ghost")} text-xs`}
                      >
                        Watch previous encounter
                      </button>
                    </div>
                  )}

                  <h3 className="font-cinzel text-2xl text-amber-200">Choose the next encounter</h3>
                  <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {run.choices.map((choice) => (
                      <button
                        key={choice.id}
                        data-testid={`sands-choice-${choice.id}`}
                        disabled={busy}
                        onClick={() => act("/sands/fight", { choice_id: choice.id, tactics })}
                        className="border border-zinc-700 bg-zinc-950/75 p-4 text-left hover:-translate-y-1 hover:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-[transform,border-color] duration-200"
                      >
                        <div className="text-xs uppercase tracking-[0.16em] text-amber-400">
                          {choice.title} · {choice.risk}
                          {choice.sands_ease_reduction > 0 && <span data-testid={`sands-choice-event-strength-${choice.id}`} className="text-cyan-200"> · Sands Ease: <span className="text-zinc-500 line-through">{choice.pre_event_opponent_score}</span><span className="ml-1">{choice.opponent_score}</span> strength</span>}
                        </div>
                        <div className="mt-2 font-cinzel text-xl text-zinc-100">{choice.opponent_name}</div>
                        <div className="text-sm text-zinc-400">
                          {choice.opponent_class} · {choice.opponent_score} strength
                        </div>
                        <p className="mt-3 text-xs text-zinc-300">
                          {choice.twist}
                          {choice.guaranteed && " · guaranteed opening win"}
                        </p>
                        {choice.locks_in_reward && (
                          <div className="mt-2 flex items-center gap-1.5 text-xs font-cinzel uppercase tracking-wide text-emerald-400">
                            <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400" />
                            Locks in reward on win
                          </div>
                        )}
                        <div className="mt-4">
                          <RewardLine rewards={{ ...choice.rewards, expedition_marks: choice.expedition_marks }} />
                        </div>
                        {choice.expedition_marks > 0 && <div data-testid={`sands-choice-marks-${choice.id}`} className="mt-2 text-xs text-amber-300">+{choice.expedition_marks} Expedition Marks on victory</div>}
                        {(choice.kit_effects || []).map((effect) => <div key={effect} data-testid={`sands-choice-kit-effect-${choice.id}`} className="mt-2 border-l-2 border-emerald-600 pl-2 text-[11px] text-emerald-300">{effect}</div>)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Won state ── */}
              {run.status === "won" && (
                <div
                  data-testid="sands-victory"
                  className="border border-emerald-800/50 bg-emerald-950/20 p-5"
                >
                  <h3 className="font-cinzel text-2xl text-emerald-200">Victory</h3>
                  {lockedThroughDepth > 0 && (
                    <div
                      data-testid="sands-auto-locked-badge"
                      className="mt-2 inline-flex items-center gap-2 border border-emerald-600/60 bg-emerald-950/40 px-3 py-1.5 text-xs font-cinzel uppercase tracking-widest text-emerald-300"
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                      {lockStatusText}
                    </div>
                  )}
                  <p className="mt-2 text-sm text-zinc-300">{run.result?.message}</p>
                  {battle && (
                    <button
                      data-testid="sands-watch-replay-button"
                      onClick={() => setReplayOpen(true)}
                      className={`${btn("ghost")} mt-3 text-xs`}
                    >
                      Watch the fight again
                    </button>
                  )}
                  <div className="mt-4 flex flex-wrap gap-2">
                    {hasUnclaimedRewards && (
                      <button
                        data-testid="sands-cash-out-button"
                        disabled={busy}
                        onClick={() => act("/sands/cash-out")}
                        className={btn("primary")}
                      >
                        Cash Out &amp; Secure Rewards
                      </button>
                    )}
                    <button
                      data-testid="sands-continue-button"
                      disabled={busy}
                      onClick={() => act("/sands/continue")}
                      className={lockedThroughDepth > 0 && !hasUnclaimedRewards ? btn("primary") : btn("ghost")}
                    >
                      {run.depth >= 5
                        ? "Complete Run"
                        : lockedThroughDepth > 0 && !hasUnclaimedRewards
                        ? `Enter Round ${run.depth + 1}`
                        : "Press Deeper"}
                    </button>
                  </div>
                </div>
              )}

              {/* ── End states (lost / cashed_out / complete) ── */}
              {terminalRun && (
                <TerminalOutcome
                  run={run}
                  resetRemaining={resetRemaining}
                  battle={battle}
                  onReplay={() => setReplayOpen(true)}
                  adminTestMode={data.admin_test_mode}
                  busy={busy}
                  onAdminRestart={resetAdminRun}
                />
              )}
            </main>

            {/* ── Rewards sidebar ── */}
            {!terminalRun && (
              <aside data-testid="sands-rewards-panel" className="h-fit border border-amber-900/40 bg-zinc-950 p-5">
                <div className="text-xs uppercase tracking-[0.16em] text-amber-400">Current rewards</div>
                {!(run.claimed?.length || run.unclaimed?.length) && (
                  <p data-testid="sands-no-current-rewards" className="mt-3 text-sm text-zinc-500">
                    Win an encounter to add rewards here.
                  </p>
                )}
                <div className="mt-3 text-sm text-zinc-400">Secured</div>
                <div className="space-y-2">
                  {(run.claimed || []).map((r, i) => (
                    <RewardLine key={`c${i}`} rewards={r} />
                  ))}
                </div>
                <div className="mt-4 text-sm text-zinc-400">At risk</div>
                <div className="space-y-2">
                  {(run.unclaimed || []).map((r, i) => (
                    <RewardLine key={`u${i}`} rewards={r} />
                  ))}
                </div>
              </aside>
            )}
          </div>
        )}

        <ThisWeeksChampions champions={data.champions} />
      </div>

      {/* ── Battle Replay Modal (same component as PvP) ── */}
      <BattleReplay
        open={replayOpen}
        battle={battle}
        onClose={() => setReplayOpen(false)}
        title="Sands Encounter"
      />
    </section>
  );
}
